<template>
  <div class="bar-chart-wrap" v-if="headers.length && groups.length">
    <div class="legend">
      <div v-for="h in headers" :key="h.key" class="legend-item">
        <span class="swatch" :style="{ background: colorFor(h.key) }"></span>
        <span>{{ h.label }}</span>
      </div>
    </div>

    <div class="svg-scroller">
      <svg :width="chartWidth" :height="height">
        <g :transform="`translate(${margin.left},${margin.top})`">
          <g v-for="t in yTicks" :key="`t-${t}`">
            <line :x1="0" :y1="valueToY(t)" :x2="chartInnerWidth" :y2="valueToY(t)" class="grid"/>
            <text :x="-6" :y="valueToY(t)" text-anchor="end" dominant-baseline="middle" class="axis-label">
              {{ formatTick(t) }}
            </text>
          </g>

          <g v-for="(g, gi) in groups" :key="g.channelId" :transform="`translate(${gi * (groupWidth + groupGap)},0)`">
            <g v-for="(h, hi) in headers" :key="`b-${g.channelId}-${h.key}`">
              <rect
                :x="barGroupOffset + hi * (barWidth + innerPad)"
                :y="valueToY(g.values[h.key])"
                :width="barWidth"
                :height="valueToH(g.values[h.key])"
                :fill="colorFor(h.key)"
                rx="2" ry="2"
              >
                <title>{{ g.channelLabel }} • {{ h.label }}: {{ formatValue(g.values[h.key]) }}</title>
              </rect>
            </g>
            
            <text :x="groupWidth/2" :y="chartInnerHeight + 30" text-anchor="middle" class="axis-label">
              {{ shortLabel(g.channelLabel) }}
            </text>
          </g>
        </g>
      </svg>
    </div>
  </div>
  <div v-else class="hint">No data</div>
</template>

<script>
export default {
  name: "BarChart",
  props: {
    headers: { type: Array, required: true },
    groups:  { type: Array, required: true },
    height:  { type: Number, default: 280 },
    margin:  { type: Object, default: () => ({ top: 10, right: 16, bottom: 36, left: 44 }) },
    barWidth:{ type: Number, default: 14 },
    innerPad:{ type: Number, default: 6 },    // between bars in a group
    groupGap:{ type: Number, default: 18 },   // between channel groups
    minGroupWidth: { type: Number, default: 90 }, // dedicated width per channel
    colorsByKey: {
      type: Object,
      default: () => ({
        instant_off_value:  "#2563eb",
        "4_hour_value":     "#16a34a",
        "10_hour_value":    "#f59e0b",
        "24_hour_value":    "#ef4444",
        max_depolarization: "#8b5cf6",
        last_depolarization:"#0891b2",
        voltage_applied:    "#84cc16",
        voltage_remaining:  "#f97316",
      }),
    },
    palette: {
      type: Array,
      default: () => ["#2563eb","#16a34a","#f59e0b","#ef4444","#8b5cf6","#0891b2","#84cc16","#f97316"],
    },
  },
  computed: {
    chartInnerHeight() { return this.height - this.margin.top - this.margin.bottom; },

    barsBlockWidth() {
      const n = this.headers.length;
      return n * this.barWidth + (n - 1) * this.innerPad;
    },
    barGroupOffset() {
      return Math.max(0, (this.groupWidth - this.barsBlockWidth) / 2);
    },
    groupWidth() {
      return Math.max(this.barsBlockWidth, this.minGroupWidth);
    },
    chartInnerWidth() {
      const n = this.groups.length;
      if (!n) return 0;
      return n * this.groupWidth + (n - 1) * this.groupGap;
    },
    chartWidth() { return this.chartInnerWidth + this.margin.left + this.margin.right; },

    maxValue() {
      let m = 0;
      for (const g of this.groups) for (const h of this.headers) {
        const v = g.values?.[h.key];
        if (Number.isFinite(v)) m = Math.max(m, v);
      }
      return m || 1;
    },

    yStep() {
      return this.maxValue <= 100 ? 10 : 20;
    },
    yMax() {
      return Math.max(this.yStep, Math.ceil(this.maxValue / this.yStep) * this.yStep);
    },
    yTicks() {
      const ticks = [];
      for (let v = 0; v <= this.yMax; v += this.yStep) ticks.push(v);
      return ticks;
    },
  },

  methods: {
    colorFor(key) {
      if (this.colorsByKey && this.colorsByKey[key]) return this.colorsByKey[key];
      const i = Math.abs(this.hashStr(String(key))) % this.palette.length;
      return this.palette[i];
    },
    hashStr(s) {
      let h = 2166136261;
      for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h += (h<<1)+(h<<4)+(h<<7)+(h<<8)+(h<<24); }
      return h | 0;
    },
    valueToY(v) {
      if (!Number.isFinite(v)) return this.margin.top + this.chartInnerHeight;
      return this.margin.top + (1 - v / this.yMax) * this.chartInnerHeight;
    },
    valueToH(v) {
      if (!Number.isFinite(v)) return 0;
      return (v / this.yMax) * this.chartInnerHeight;
    },
    shortLabel(label) {
      const s = String(label ?? "");
      return s.length > 18 ? "…" + s.slice(-17) : s;
    },
    formatValue(v) {
      if (v == null) return "-";
      if (Number.isFinite(v)) return v.toFixed(3);
      return v;
    },
    formatTick(t) { return t.toFixed(0); },
  },
};
</script>

<style scoped>
.bar-chart-wrap { display: flex; flex-direction: column; gap: 8px; }
.legend { display: flex; flex-wrap: wrap; gap: 10px; font-size: 12px; }
.legend-item { display: inline-flex; align-items: center; gap: 6px; }
.legend-item .swatch { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
.svg-scroller { overflow-x: auto; border: 1px solid #eee; border-radius: 6px; }
.grid { stroke: #e5e7eb; stroke-width: 1; }
.axis-label { fill: #6b7280; font-size: 11px; }
.hint { margin-top: 6px; color: #666; }
</style>
